import { useAuthStore } from "@utils/stores/authStore.ts";

export function isWithinLastWeek(date: Date): boolean {
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7); // Set to 7 days ago
  return date >= oneWeekAgo;
}

export function isWithinLastMonth(date: Date): boolean {
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1); // Set to 1 month ago
  return date >= oneMonthAgo;
}

export function validateUrl(url: string): boolean {
  const urlRegex =
    /^(https?:\/\/)?([a-zA-Z\d-]+\.)*[a-zA-Z\d-]{2,63}\.[a-z]{2,6}(\/[\w.-]*)*\/?$/;

  return urlRegex.test(url);
}

export function injectFreshchatBot() {
  (function (w, d, s, l, i) {
    type WindowWithGTM = typeof window & { [key: string]: any };
    type DocumentWithGTM = Document & {
      getElementById(id: string): HTMLElement | null;
    };

    const win = w as WindowWithGTM;
    const doc = d as DocumentWithGTM;

    win[l] = win[l] || [];
    win[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

    const f = doc.getElementsByTagName(s)[0] as HTMLScriptElement;
    const j = doc.createElement(s) as HTMLScriptElement;
    const dl = l !== "dataLayer" ? "&l=" + l : "";

    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;

    f?.parentNode?.insertBefore(j, f);
  })(window, document, "script", "dataLayer", "GTM-M8WXWK6R");
}

export const generateRandomColor = (colors: string[]) => {
  return colors[Math.floor(Math.random() * colors.length)];
};

/**
 * Generates the current tax year. Tax years typically run from January to December, but since we
 * don't start preparing taxes until November, we consider the current year to be the tax year if
 * the current month is after November.
 *
 * @returns The current tax year as a number.
 */
export const generateTaxYear = (): number => {
  const now = new Date(); // Get the current date
  const currentYear = now.getFullYear(); // Extract the year
  const currentMonth = now.getMonth(); // Get the month (0-indexed: 0 = Jan, 11 = Dec)
  let taxYear = currentYear;

  if (currentMonth < 11) {
    taxYear = currentYear - 1;
  }

  // Return the tax year as a string
  return taxYear;
};

/**
 * Formats a date string to MM/DD/YYYY format.
 *
 * @param dateValue - The date string to format.
 * @returns The formatted date string in MM/DD/YYYY format.
 */
export const formatDateToMMDDYYYY = (dateValue: string | number): string => {
  if (!dateValue) return "";
  try {
    const date = new Date(dateValue).toISOString().split("T")[0]; // Gets YYYY-MM-DD
    const [year, month, day] = date.split("-");
    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

/**
 * Formats a date string to Month Day, Year format.
 *
 * @param dateString - The date string to format.
 * @returns The formatted date string in Month Day, Year format.
 */
export const fancyFormatDate = (dateString?: string): string => {
  if (!dateString) return "Not available";
  // Extract just the date portion from ISO string
  const [year, month, day] = new Date(dateString)
    .toISOString()
    .split("T")[0]
    .split("-");

  // Create date using local components to avoid timezone shift
  const date = new Date(Number(year), Number(month) - 1, Number(day));

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

/**
 * Retrieves the business information from authStore.
 *
 * @returns An object containing the business information, or an empty object if not available.
 */
export const getBusinessInfo = () => {
  try {
    const businessInfo = useAuthStore.getState().businessInfo;
    if (!businessInfo) return {};

    return {
      businessId: businessInfo.businessId,
      businessName: businessInfo.businessName,
      detailedFlow: businessInfo.detailedFlow ?? true,
      packageName: businessInfo.packageName,
      logoUrl: businessInfo.logoUrl,
    };
  } catch {
    return {};
  }
};

export const getRole = () => {
  try {
    // Import dynamically to avoid circular dependencies
    const role = useAuthStore.getState().role;
    return role ?? "";
  } catch {
    return "";
  }
};

/**
 * Gets the business logo URL from sessionStorage.
 *
 * @returns The business logo URL as a string. If no logo is set, an empty string is returned.
 */

export const getBusinessLogo = () => {
  const logoUrl = sessionStorage.getItem("logoUrl");
  return logoUrl !== "undefined" ? logoUrl : "";
};
